export const RADII = [5, 10, 25, 50, 75, 100] as const;
export type Radius = (typeof RADII)[number];
export type DateFilter = '' | 'today' | 'tomorrow' | 'weekend';

export interface MapState {
  lat: number;
  lng: number;
  z: number;
  r: Radius;
  /** type-group keys (see TYPE_GROUPS) */
  types: string[];
  date: DateFilter;
  /** 'radius' = search around lat/lng; 'bbox' = search the visible map */
  mode: 'radius' | 'bbox';
  /** a search has been made (vs. the initial national view) */
  active: boolean;
}

export const DEFAULT_STATE: MapState = { lat: 39.5, lng: -98.35, z: 3.4, r: 25, types: [], date: '', mode: 'radius', active: false };

const RADIUS_KEY = 'ysm.radius.v1';

export function storedRadius(): Radius {
  try {
    const r = Number(localStorage.getItem(RADIUS_KEY));
    return (RADII as readonly number[]).includes(r) ? (r as Radius) : 25;
  } catch {
    return 25;
  }
}

export function storeRadius(r: Radius) {
  try {
    localStorage.setItem(RADIUS_KEY, String(r));
  } catch {
    /* ignore */
  }
}

const LAST_LIST_KEY = 'ysm.lastList.v1';

/** The last search-list URL this tab showed, so Back can return to it from a listing. */
export function saveLastList(url: string) {
  try {
    sessionStorage.setItem(LAST_LIST_KEY, url);
  } catch {
    /* ignore */
  }
}

export function readLastList(): string | null {
  try {
    return sessionStorage.getItem(LAST_LIST_KEY);
  } catch {
    return null;
  }
}

/** True for the home page with an active search (the list of sales), not the blank national map. */
export function isListUrl(u: string | null | undefined): boolean {
  if (!u) return false;
  try {
    const x = new URL(u, 'https://ysm.invalid');
    return x.pathname === '/' && x.searchParams.has('lat') && x.searchParams.has('lng');
  } catch {
    return false;
  }
}

export type BackTarget = { action: 'history-back' } | { action: 'navigate'; to: string };

/**
 * Where "Back to list" on a listing goes. Decided from state the app records itself, never from
 * document.referrer (which is fixed at page load, so visitors who arrive from Facebook or Google
 * would always be sent to a blank map).
 *  1. the app opened this listing from the search list: step back in history
 *  2. this tab remembers a search list (listing reloaded, opened another way): go to it
 *  3. no search at all: list the sales around this listing
 */
export function backTarget(o: { from?: string | null; lastList?: string | null; lat: number; lng: number }): BackTarget {
  if (isListUrl(o.from)) return { action: 'history-back' };
  if (o.lastList && isListUrl(o.lastList)) return { action: 'navigate', to: o.lastList };
  const near: MapState = { ...DEFAULT_STATE, lat: o.lat, lng: o.lng, z: 9.6, r: 25, active: true };
  return { action: 'navigate', to: `/${writeState(near)}` };
}

export function readState(search: string): MapState {
  const p = new URLSearchParams(search);
  const num = (k: string, d: number) => {
    const v = Number(p.get(k));
    return p.has(k) && Number.isFinite(v) ? v : d;
  };
  const r = Number(p.get('r'));
  const date = p.get('date');
  const active = p.has('lat') && p.has('lng');
  return {
    lat: num('lat', DEFAULT_STATE.lat),
    lng: num('lng', DEFAULT_STATE.lng),
    z: num('z', DEFAULT_STATE.z),
    r: (RADII as readonly number[]).includes(r) ? (r as Radius) : 25,
    types: (p.get('types') ?? '').split(',').filter(Boolean),
    date: date === 'today' || date === 'tomorrow' || date === 'weekend' ? date : '',
    mode: p.get('mode') === 'bbox' ? 'bbox' : 'radius',
    active,
  };
}

export function writeState(s: MapState): string {
  if (!s.active) return '';
  const p = new URLSearchParams();
  p.set('lat', s.lat.toFixed(5));
  p.set('lng', s.lng.toFixed(5));
  p.set('z', s.z.toFixed(2));
  p.set('r', String(s.r));
  if (s.types.length) p.set('types', s.types.join(','));
  if (s.date) p.set('date', s.date);
  if (s.mode === 'bbox') p.set('mode', 'bbox');
  return `?${p.toString()}`;
}
